/* eslint-disable multiline-comment-style */
import { motion, type Variants } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Dialog, DialogButton, DialogDescription, DialogRoot, DialogTitle } from '~/components/ui/Dialog';
import { db, projectId, deleteByProjectId, getAll } from '~/lib/persistence';
import { cubicEasingFn } from '~/utils/easings';
import { json, type LoaderFunction } from '@remix-run/cloudflare';
import { logger } from '~/utils/logger';
import { HistoryItem } from './HistoryItem';
import { binDates } from './date-binning';
import { Plus, ExternalLink, X } from 'lucide-react';
import { useStore } from '@nanostores/react';
import { userStore } from '~/lib/stores/user';
import * as Sentry from '@sentry/remix';
import { planNameCleaner } from '~/lib/utils/planNameCleaner';
import { useDebounce } from '~/hooks/useDebounce';
import { useTranslation } from '~/lib/hooks/useTranslation';
import type { CloudChatHistoryItem, ChatHistoryItem } from '~/services/interfaceService';
import { Loading } from '../chat/Loading';

export const loader: LoaderFunction = async ({ context }) => {
  const baseUrl =
    process.env.NEXT_PUBLIC_GET_ACCESS_URL_DEV ||
    process.env.NEXT_PUBLIC_GET_ACCESS_URL ||
    context.cloudflare.env.NEXT_PUBLIC_GET_ACCESS_URL;

  return json(baseUrl);
};

const menuVariants = {
  closed: {
    opacity: 0,
    visibility: 'hidden',
    left: '-150px',
    transition: {
      duration: 0.2,
      ease: cubicEasingFn,
    },
  },
  open: {
    opacity: 1,
    visibility: 'initial',
    left: 0,
    transition: {
      duration: 0.2,
      ease: cubicEasingFn,
    },
  },
} satisfies Variants;

type DialogContent = { type: 'delete'; item: CloudChatHistoryItem } | null;

export function Menu({
  open,
  onClose,
  projects = [],
}: {
  open: boolean;
  onClose?: () => void;
  projects?: CloudChatHistoryItem[];
}) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [list, setList] = useState<CloudChatHistoryItem[]>([]);
  const [dialogContent, setDialogContent] = useState<DialogContent>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadEntries = useCallback(async () => {
    if (projects && projects.length > 0) {
      const visibleProjects = projects.filter((project) => {
        if (!project.projectId || !project.projectName) {
          return false;
        }

        return true;
      });

      console.log('[Menu] menu visibleProjects:', visibleProjects);

      setList(visibleProjects);
    } else if (db) {
      console.log('[Menu] Loading projects from IndexedDB');
    }
  }, [projects]);

  const deleteItem = useCallback(
    (event: React.UIEvent, item: CloudChatHistoryItem) => {
      event.preventDefault();

      if (db) {
        if (item.projectId) {
          deleteByProjectId(db, item.projectId)
            .then(() => {
              loadEntries();

              if (projectId.get() === item.projectId) {
                // hard page navigation to clear the stores
                window.location.pathname = '/';
              }
            })
            .catch((error) => {
              Sentry.captureException(error, {
                extra: {
                  message: 'Error deleting chat history',
                  item,
                },
              });
              toast.error('Failed to delete conversation');
              logger.error(error);
            });
        }
      } else {
        // If using server projects, we need to implement server-side deletion
        console.warn('[Menu] Server project deletion not yet implemented');
        toast.error('Project deletion not yet implemented for server projects');
      }
    },
    [loadEntries],
  );

  const closeDialog = () => {
    setDialogContent(null);
  };

  const handleLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  useEffect(() => {
    if (open) {
      loadEntries();
    }
  }, [open, loadEntries]);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  return (
    <>
      {/* Full-screen loader overlay */}
      {isLoading && <Loading heading="Loading your Project" screen={0} />}

      <motion.div
        ref={menuRef}
        initial="closed"
        animate={open ? 'open' : 'closed'}
        variants={menuVariants}
        className="flex flex-col side-menu fixed top-[8vh] w-[90vw] md:w-[20vw] ml-4 mt-1 h-90vh bg-alpha-background-primary p-4 rounded-xl border border-alpha-border-dark z-sidebar shadow-xl shadow-alpha-elements-sidebar-dropdownShadow text-sm"
      >
        <div
          className="flex-1 flex flex-col h-full w-full overflow-y-scroll "
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--alpha-background-tertiary) var(--alpha-background-primary)',
            paddingRight: '5px',
          }}
        >
          <div className="sticky top-0 z-10 bg-alpha-background-primary">
            <div className="w-full flex justify-between items-center mb-2">
              <span className="text-md font-medium">{t('menu.myProjects')}</span>

              <div className="flex items-center gap-2">
                {/* Close button - only visible on mobile */}
                {onClose && (
                  <button
                    onClick={onClose}
                    className="md:hidden flex items-center justify-center bg-alpha-background-tertiary text-white hover:bg-alpha-background-depth-3 rounded-md p-1 transition-theme"
                    aria-label={t('menu.closeMenu')}
                  >
                    <X />
                  </button>
                )}

                <a
                  href="/"
                  className="flex items-center justify-center bg-alpha-background-tertiary text-white hover:bg-alpha-background-depth-3 rounded-md p-1 transition-theme"
                >
                  <Plus />
                </a>
              </div>
            </div>

            {/* project settings */}
            <div className="mb-4">
              <a
                href="/my-projects"
                className="text-sm text-alpha-elements-textSecondary hover:text-alpha-elements-textPrimary transition-colors underline flex items-center gap-1"
              >
                <ExternalLink size={14} />
                {t('menu.projectSettings')}
              </a>
            </div>

            {/* project search bar */}
            <div className="relative mb-4">
              <input
                type="text"
                placeholder={t('menu.searchPlaceholder')}
                className="w-full px-3 py-2 bg-alpha-background-tertiary border border-alpha-border-dark rounded-md text-alpha-elements-textPrimary placeholder:text-alpha-elements-textTertiary focus:outline-none focus:ring-1 focus:ring-alpha-elements-borderFocus"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-alpha-elements-textTertiary">
                <div className="i-ph:magnifying-glass w-4 h-4" />
              </div>
            </div>
          </div>

          <div className="flex-1 pb-5">
            {list.length === 0 && (
              <div className="pl-2 text-alpha-elements-textTertiary">{t('menu.noConversations')}</div>
            )}
            <DialogRoot open={dialogContent !== null}>
              {binDates(list).map(({ category, items }) => (
                <div key={category} className="mt-4 first:mt-0 space-y-2">
                  <div className="text-alpha-elements-textTertiary bg-alpha-background-primary sticky top-0 z-1 pl-2 pt-2 pb-2">
                    {category}
                  </div>
                  {items
                    .filter((item) => item.projectName?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()))
                    .map((item) => (
                      <HistoryItem
                        key={item.projectId}
                        item={item}
                        onDelete={() => setDialogContent({ type: 'delete', item })}
                        onLoading={handleLoading}
                      />
                    ))}
                </div>
              ))}
              <Dialog onBackdrop={closeDialog} onClose={closeDialog}>
                {dialogContent?.type === 'delete' && (
                  <>
                    <DialogTitle>Delete Chat?</DialogTitle>
                    <DialogDescription asChild>
                      <div>
                        <p>
                          You are about to delete <strong>{dialogContent.item.projectName}</strong>.
                        </p>
                        <p className="mt-1">Are you sure you want to delete this chat?</p>
                      </div>
                    </DialogDescription>
                    <div className="px-5 pb-4 bg-alpha-elements-background-depth-2 flex gap-2 justify-end">
                      <DialogButton type="secondary" onClick={closeDialog}>
                        Cancel
                      </DialogButton>
                      <DialogButton
                        type="danger"
                        onClick={(event) => {
                          deleteItem(event, dialogContent.item);
                          closeDialog();
                        }}
                      >
                        Delete
                      </DialogButton>
                    </div>
                  </>
                )}
              </Dialog>
            </DialogRoot>
          </div>
          <div className="flex items-center border-t border-alpha-elements-borderColor pt-4 sticky bottom-0 bg-alpha-background-primary z-10">
            <UserStatus />
          </div>
        </div>
      </motion.div>
    </>
  );
}

function UserStatus() {
  const userData = useStore(userStore);

  if (!userData.email) {
    return <div></div>;
  }

  const { planName } = planNameCleaner(userData.planName);
  const isUnlimited = planName === 'Launch';

  return (
    <div className="flex items-center gap-2">
      <Avatar src={userData.email} />
      <div className="flex flex-col gap-2">
        <RemainingPrompt
          maxPrompts={userData.maxPrompts}
          promptsUsed={(parseInt(userData.promptsUsed) + parseInt(userData.topUpPromptsUsed)).toString()}
          email={userData.email}
          isUnlimited={isUnlimited}
          topUpPrompts={userData.topUpPrompts}
        />
        <div className="flex items-center gap-2 text-xs text-alpha-elements-textSecondary  transition-colors">
          <button
            className="hover:text-alpha-elements-textPrimary"
            onClick={() => {
              window.location.href = '/settings';
            }}
          >
            View Usage
          </button>
        </div>
      </div>
    </div>
  );
}

function Avatar({ src }: { src: string }) {
  const avatar = src.split('@')[0].charAt(0).toUpperCase();
  return (
    <div className="w-10 h-10 flex items-center justify-center bg-alpha-background-tertiary rounded-full p-2">
      <span className="text-white text-sm">{avatar}</span>
    </div>
  );
}

function RemainingPrompt({
  maxPrompts,
  promptsUsed,
  email,
  isUnlimited,
  topUpPrompts,
}: {
  maxPrompts: string;
  promptsUsed: string;
  email: string;
  isUnlimited: boolean;
  topUpPrompts?: string;
}) {
  // const totalPrompts = Number(maxPrompts) + Number(topUpPrompts);
  return (
    <div className="text-alpha-elements-textTertiary gap-1 flex flex-col ">
      <div className="truncate max-w-[12rem] pb-[0.5px]" title={email}>
        {email}
      </div>
      <div>
        {isUnlimited
          ? `${promptsUsed}/Unlimited`
          : Number(topUpPrompts) > 0
            ? `${promptsUsed} / ${maxPrompts} + ${topUpPrompts}`
            : `${promptsUsed} / ${maxPrompts}`}{' '}
        messages
      </div>
    </div>
  );
}
